/* eslint-disable no-multi-assign */
/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable consistent-return */
/* eslint-disable no-console */
import { isEmpty } from 'lodash';

import { IMediaState, IMutedMediaState } from '../types';

import {
  AudioCodec,
  CallState,
  GroupCallParticipantVideoSourceGroup,
  P2PAudioCodec,
  P2PVideoCodec,
  Ssrc,
  VideoCodec,
  WebRTCLineType,
} from './types';
import { P2PSdpBuilder } from './P2PSdpBuilder';
import LocalConferenceDescription, {
  ConferenceEntry,
} from './localConferenceDescription';
import { playSound } from './sound';

export class RTCClient {
  private observers: Record<string, Function[]> = {};

  private mediaState: IMediaState = {
    input: {
      videoState: 'inactive',
      muted: false,
    },
    output: {
      videoState: 'inactive',
      muted: false,
    },
  };

  private channel?: RTCDataChannel = undefined;

  private offerSent: boolean;

  private candidates: RTCIceCandidate[] | [] = [];

  public callInProgress: boolean = false;

  private peerConnection?: RTCPeerConnection = undefined;

  public inputStream: MediaStream = new MediaStream();

  public outputStream: MediaStream = new MediaStream();

  private description: LocalConferenceDescription;

  isConnected(): boolean {
    return this.callInProgress;
  }

  getConnection() {
    return this.peerConnection;
  }

  getIsMuted(): IMutedMediaState {
    return {
      video: {
        me: this.mediaState.input.videoState === 'inactive',
        them: this.mediaState.output.videoState === 'inactive',
      },
      audio: {
        me: this.mediaState.input.muted,
        them: this.mediaState.output.muted,
      },
    };
  }

  subscribe(action: string, fn: Function) {
    if (this.observers[action] === undefined) {
      this.observers[action] = [];
    }

    this.observers[action].push(fn);
  }

  unsubscribe(action: string, fnToRemove: Function) {
    if (this.observers[action]) {
      this.observers[action] = this.observers[action].filter(
        (fn) => fn !== fnToRemove
      );
    }
  }

  fire(action: string, ...args: any[]) {
    console.log(`RTCClient[${action}]:`, this.peerConnection, ...args);

    if (this.observers[action]) {
      this.observers[action].forEach((fn) => {
        fn(...args);
      });
    }
  }

  async connect(currentCall: CallState): Promise<void> {
    try {
      console.log(`RTCClient[connect]`, currentCall);
      this.callInProgress = true;

      this.mediaState.input = {
        '@type': 'MediaState',
        muted: false,
        videoState: currentCall.is_video ? 'active' : 'inactive',
        // lowBattery: false, ??
      };
      const { configuration } = currentCall;

      if (!this.peerConnection) {
        this.peerConnection = new RTCPeerConnection(configuration);
        this.peerConnection.addEventListener(
          'icegatheringstatechange',
          this.onIceGatheringStateChange.bind(this),
          true
        );
        this.peerConnection.addEventListener(
          'icecandidate',
          this.onIceCandidate.bind(this),
          true
        );
        this.peerConnection.addEventListener(
          'iceconnectionstatechange',
          this.onIceConnectionStateChange.bind(this),
          true
        );
        this.peerConnection.addEventListener(
          'connectionstatechange',
          this.onConnectionStateChange.bind(this),
          true
        );
        this.peerConnection.addEventListener(
          'signalingstatechange',
          this.onSignalingStateChange.bind(this),
          true
        );
        this.peerConnection.addEventListener(
          'track',
          this.onTrack.bind(this),
          true
        );
        this.peerConnection.addEventListener(
          'negotiationneeded',
          this.onNegotiationNeeded.bind(this, currentCall),

          true
        );

        this.peerConnection.addEventListener(
          'icecandidateerror',
          this.onIceError.bind(this),
          true
        );
      }

      this.description = new LocalConferenceDescription(this.peerConnection);
      const videoEnable = this.mediaState.input.videoState === 'active';

      const inputStream = await navigator.mediaDevices.getUserMedia({
        video: videoEnable,
        audio: { echoCancellation: true, autoGainControl: true },
      });

      if (this.mediaState.input.videoState === 'inactive') {
        inputStream.getVideoTracks().forEach((track) => {
          track.enabled = false;
        });
      }

      this.inputStream = inputStream;
      const inputVideo = document.getElementById(
        'call-input-video'
      ) as HTMLMediaElement;

      if (inputVideo) {
        inputVideo.volume = 0;
        // inputVideo.srcObject = inputStream;
      }

      this.p2pAppendInputStream(inputStream);
    } catch (error) {
      console.error('error during streaming setup', error);
      this.stopAllStreams();
      this.closePeerConnection(this.peerConnection);
    }
  }

  onIceGatheringStateChange() {
    const iceGatheringState = this.peerConnection?.iceGatheringState;
    this.fire('iceGatheringStateChange', iceGatheringState);
  }

  async onNegotiationNeeded() {
    const iceGatheringState = this.peerConnection?.iceGatheringState;

    const offer = await this.peerConnection?.createOffer();
    this.offerSent = true;

    this.fire('send-sdp', offer);
    await this.peerConnection?.setLocalDescription(offer);
    this.fire('iceGatheringStateChange', iceGatheringState);
  }

  onConnectionStateChange(): void {
    const connectionState = this.peerConnection?.connectionState;

    this.fire('connectionStateChange', connectionState);
  }

  onSignalingStateChange(): void {
    const signalingState = this.peerConnection?.signalingState;
    this.fire('signalingStateChange', signalingState);
  }

  onIceError(e): void {
    this.fire('icecandidateerror', e);
  }

  async onIceCandidate(event: RTCPeerConnectionIceEvent): Promise<void> {
    const { sdpMLineIndex } = event.candidate || {};

    if (sdpMLineIndex !== 0) {
      return;
    }

    this.fire('iceCandidate', event);
  }

  async onDiscardCall(): Promise<void> {
    this.callInProgress = false;
    this.closePeerConnection(this.peerConnection);
    playSound('end');
  }

  onIceConnectionStateChange(): void {
    const iceConnectionState = this.peerConnection?.iceConnectionState;
    this.fire('iceConnectionStateChange', iceConnectionState);

    if (
      this.peerConnection?.iceConnectionState === 'failed' ||
      this.peerConnection?.iceConnectionState === 'closed'
    ) {
      this.stopAllStreams();
      this.closePeerConnection(this.peerConnection);
    }

    if (iceConnectionState === 'connected') {
      playSound('connect');
    }
  }

  onTrack(event: RTCTrackEvent): void {
    const { track } = event;
    this.outputStream.addTrack(track);
    const video = document.getElementById('call-output-video') as
      | HTMLMediaElement
      | undefined;

    if (video) {
      // video.srcObject = this.outputStream;
      // video.autoplay = true;
      // video.volume = 1.0;

      if (video.paused) {
        video
          .play()
          .then((_) => {})
          .catch((_e) => {});
      }
    }

    if (track.kind === 'audio') {
      const audio = document.getElementById('call-output-audio') as
        | HTMLMediaElement
        | undefined;

      if (audio) {
        audio.srcObject = this.outputStream;
        audio.autoplay = true;
        audio.volume = 1.0;
      }
    }

    this.fire('onTrack', event, this.outputStream.getTracks(), video);

    track.onmute = () => {
      // console.log('track.onmute', track);
    };
    track.onunmute = () => {
      // console.log('track.onunmute', track);
    };
  }

  stopAllStreams(): void {
    this.stopStream(
      document.getElementById('call-output-video') as HTMLMediaElement
    );
    this.stopStream(
      document.getElementById('call-input-video') as HTMLMediaElement
    );
    this.stopStream(
      document.getElementById('call-audio-video') as HTMLMediaElement
    );
  }

  stopStream(element: HTMLMediaElement) {
    if (element) {
      const srcObject = element.srcObject as MediaStream;

      if (srcObject) srcObject.getTracks().forEach((track) => track.stop());

      element.srcObject = null;
    }
  }

  closePeerConnection(pc?: RTCPeerConnection): void {
    if (!pc) return;

    if (this.inputStream) {
      this.inputStream.getTracks().forEach((track) => track.stop());
      this.inputStream = new MediaStream();
    }

    pc.close();
    pc.removeEventListener(
      'icegatheringstatechange',
      this.onIceGatheringStateChange,
      true
    );
    pc.removeEventListener('icecandidate', () => this.onIceCandidate, true);
    pc.removeEventListener(
      'iceconnectionstatechange',
      this.onIceConnectionStateChange,
      true
    );
    pc.removeEventListener(
      'connectionstatechange',
      this.onConnectionStateChange,
      true
    );
    pc.removeEventListener(
      'signalingstatechange',
      this.onSignalingStateChange,
      true
    );

    pc.removeEventListener(
      'icecandidateerror',
      this.onIceError.bind(this),
      true
    );

    pc.removeEventListener('track', () => this.onTrack, true);
    this.stopAllStreams();

    if (pc === this.peerConnection) {
      this.peerConnection = undefined;
    }

    this.fire('close');
  }

  async p2pApplyCallSignalingData(data): Promise<void> {
    if (!data || !this.peerConnection) return;

    const type = data['@type'] || data.type;
    console.log(`RTCClient[p2pApplyCallSignalingData]:`, data);
    switch (type) {
      case 'InitialSetup': {
        try {
          const _data = { ...data };

          this.description.setData({
            transport: {
              pwd: _data.pwd,
              ufrag: _data.ufrag,
              fingerprints: _data.fingerprints,
              'rtcp-mux': true,
            },
            audio: this.performCodec(_data.audio),
            video: _data.video
              ? (this.performCodec(_data.video) as VideoCodec)
              : undefined,
            screencast: _data.screencast
              ? (this.performCodec(_data.screencast) as VideoCodec)
              : undefined,
          });

          const performSsrcGroups = (
            ssrcGroups: P2PVideoCodec['ssrcGroups']
          ): GroupCallParticipantVideoSourceGroup[] =>
            ssrcGroups.map((ssrcGroup) => ({
              _: 'groupCallParticipantVideoSourceGroup',
              semantics: ssrcGroup.semantics,
              sources: ssrcGroup.ssrcs.map((source) => +source),
            }));

          const ssrcs = [
            this.generateSsrc('audio', +_data.audio.ssrc),
            _data.video
              ? this.generateSsrc(
                  'video',
                  performSsrcGroups(_data.video.ssrcGroups)
                )
              : undefined,
            _data.screencast
              ? this.generateSsrc(
                  'screencast',
                  performSsrcGroups(_data.screencast.ssrcGroups)
                )
              : undefined,
          ].filter(Boolean);

          ssrcs.forEach((ssrc) => {
            let entry = this.description.getEntryBySource(ssrc!.source);

            if (entry) {
              return;
            }

            const sendRecvEntry = this.description.findFreeSendRecvEntry(
              ssrc!.type,
              false
            );
            entry = new ConferenceEntry(sendRecvEntry.mid, ssrc!.type);
            entry.setDirection('sendrecv');
            sendRecvEntry.recvEntry = entry;

            this.description.setEntrySource(
              entry,
              ssrc!.sourceGroups || ssrc!.source
            );
          });

          this.createDataChannelEntry();

          const isAnswer = this.offerSent;

          const bundle = this.description.entries.map((entry) => entry.mid);
          const sdpDescription: RTCSessionDescriptionInit = {
            type: isAnswer ? 'answer' : 'offer',
            sdp: this.description.generateSdp({
              bundle,
              entries: this.description.entries.filter((entry) =>
                bundle.includes(entry.mid)
              ),
              // isAnswer: isAnswer
              isAnswer: !isAnswer,
            }),
          };

          this.offerSent = false;

          console.log('RTCClient[description]:', sdpDescription);
          try {
            await this.peerConnection.setRemoteDescription(sdpDescription);
          } catch (e) {
            console.error(e);
          }

          console.log('RTCClient[description-finish]');

          if (
            this.peerConnection.remoteDescription &&
            !isEmpty(this.candidates)
          ) {
            await Promise.all(
              this.candidates.map((x) =>
                this.peerConnection?.addIceCandidate(x)
              )
            ).then(() => {
              this.candidates = [];
            });
          }
        } catch (e) {
          console.error(e);
        }

        break;
      }
      case 'Candidates': {
        const candidates = data.candidates.map((x) => {
          const candidate = P2PSdpBuilder.generateCandidate(x);
          candidate!.sdpMLineIndex = 0;

          return candidate;
        });

        if (candidates.length > 0) {
          await Promise.all(
            candidates.map((x) => {
              if (!this.peerConnection?.remoteDescription) {
                this.fire('Candidates', x);
                this.candidates = [...this.candidates, x];

                return Promise.resolve();
              }

              console.log(`RTCClient[addIceCandidate]`, x);

              return this.peerConnection.addIceCandidate(x);
            })
          );
        }

        break;
      }
      case 'hungUpCall': {
        await this.onDiscardCall();
        this.fire('connectionStateChange', 'disconnected');

        break;
      }
      case 'callStateReady': {
        this.fire('connectionStateChange', 'callPendingIsFinished');

        break;
      }
    }
  }

  private createDataChannelEntry() {
    const dataChannelEntry = this.description.createEntry('application');
    dataChannelEntry.setDirection('sendrecv');
    dataChannelEntry.sendEntry = dataChannelEntry.recvEntry = dataChannelEntry;
  }

  generateSsrc(
    type: WebRTCLineType,
    source: number | GroupCallParticipantVideoSourceGroup[],
    endpoint?: string
  ): Ssrc | undefined {
    let sourceGroups: GroupCallParticipantVideoSourceGroup[];

    if (Array.isArray(source)) {
      if (!source[0]) return;

      sourceGroups = source;
      source = sourceGroups[0].sources[0];
    }

    return {
      endpoint,
      type,
      source,
      // @ts-ignore
      sourceGroups,
    };
  }

  private performCodec(_codec: P2PAudioCodec | P2PVideoCodec) {
    const payloadTypes: AudioCodec['payload-types'] = _codec.payloadTypes.map(
      (payloadType) => ({
        ...payloadType,
        'rtcp-fbs': payloadType.feedbackTypes,
      })
    );

    const codec: AudioCodec = {
      'rtp-hdrexts': _codec.rtpExtensions,
      'payload-types': payloadTypes,
    };
    console.log('RTCClient[codec]:', codec);

    return codec;
  }

  black = ({ width = 640, height = 480 } = {}) => {
    const canvas = Object.assign(document.createElement('canvas'), {
      width,
      height,
    });
    const ctx = canvas.getContext('2d');

    if (!ctx) throw Error('Cannot create canvas ctx');

    ctx.fillRect(0, 0, width, height);
    const stream = canvas.captureStream();

    return new MediaStream([
      Object.assign(stream.getVideoTracks()[0], { enabled: false }),
    ]);
  };

  p2pAppendInputStream(inputStream: MediaStream): void {
    const senders = this.peerConnection?.getSenders();

    if (senders?.some((x) => x.track)) return;

    const video = this.black({ width: 640, height: 480 });
    const screenshare = this.black({ width: 640, height: 480 });

    inputStream.getAudioTracks().forEach((x) => {
      this.peerConnection?.addTrack(x, inputStream);
    });
    this.peerConnection?.addTrack(video.getTracks()[0], video);
    this.peerConnection?.addTrack(screenshare.getTracks()[0], screenshare);
    /*
    inputStream.getVideoTracks().forEach((x) => {
      this.peerConnection?.addTrack(x, inputStream);
    });
    */

    this.p2pCreateChannel(this.peerConnection);
  }

  p2pCreateChannel(connection?: RTCPeerConnection): void {
    if (!connection) return;

    this.channel = connection.createDataChannel('data', {
      id: 0,
      negotiated: true,
    });
    this.channel.onmessage = (e) => {
      const { data } = e;
      console.log('RTCClient[dataChannelMessage]', e);
      this.p2pApplyCallDataChannelData(JSON.parse(data));
    };
    this.channel.onopen = (e) => {
      console.log('RTCClient[dataChannelOpen]', e);

      if (!this.mediaState.input) return;

      console.log('RTCClient[dataChannelOpen]', e);
      this.p2pSendMediaState(JSON.stringify(this.mediaState.input));
    };
    this.channel.onclose = (e) => {
      console.log('RTCClient[dataChannelClose]', e);
    };
  }

  p2pSendMediaState(mediaState: string): void {
    const { channel } = this;

    if (!channel) return;

    if (channel.readyState !== 'open') return;

    this.channel!.send(mediaState);
  }

  p2pApplyCallDataChannelData(data): void {
    if (!data) return;

    console.log(`RTCClient[p2pApplyCallDataChannelData]:`, data);
    const type = data['@type'] || data.type;
    switch (type) {
      case 'media': {
        const { kind, isMuted } = data;

        if (kind === 'audio') {
          this.mediaState.input.muted = isMuted;
        } else if (kind === 'video') {
          this.mediaState.input.videoState = isMuted ? 'inactive' : 'active';
        }

        break;
      }
      case 'MediaState': {
        if (data) {
          this.fire('changeClientMute', data.muted);
        }

        this.mediaState.output = data;

        this.outputStream.getVideoTracks().forEach((track) => {
          track.enabled = this.mediaState.output.videoState === 'active';
        });

        this.outputStream.getAudioTracks().forEach((track) => {
          track.enabled = !this.mediaState.output.muted;
        });

        break;
      }
    }
  }

  p2pSendMediaIsMuted(kind: 'video' | 'audio', isMuted: boolean): void {
    if (!this.channel) return;

    const senders = this.peerConnection?.getSenders();

    senders?.forEach((x) => {
      const { track } = x;

      if (track && track.kind === kind) {
        track.enabled = !isMuted;
      }
    });

    if (kind === 'video') {
      this.mediaState.input.videoState = !isMuted ? 'active' : 'inactive';

      this.inputStream.getVideoTracks().forEach((track: MediaStreamTrack) => {
        track.enabled = !isMuted;
      });
    }

    if (kind === 'audio') {
      this.mediaState.input.muted = isMuted;

      this.inputStream.getAudioTracks().forEach((track: MediaStreamTrack) => {
        track.enabled = !isMuted;
      });
    }

    this.p2pSendMediaState(JSON.stringify({ type: 'media', kind, isMuted }));
  }
}
